import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  HostListener
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, finalize, Subject, Subscription, switchMap, take, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { StorageService, StorageResponse } from 'src/app/pages/storage/services/storage.service';
import { OrdersService } from 'src/app/pages/orders/services/orders.service';
import { StoreStockItem, InventoryResponse, StoreStockLot, StoreStockAndLot } from 'src/app/pages/stock/interfaces/stock.interface';
import { ConfirmMessageComponent } from 'src/app/shared/confirm-message-modal/confirm-message.component';
import { StockLotModalComponent } from 'src/app/pages/stock/components/stock-lot-modal/stock-lot-modal.component';
import { UserRoleService } from 'src/app/core/services/role.service';
import { DrawerService } from 'src/app/core/services/drawer.service';
import { BulkUploadModalComponent } from 'src/app/pages/stock/components/bulk-upload-modal/bulk-upload-modal.component';
import { StockAlertsService, ProductAlert } from '../../../../shared/services/stock-alerts.service';
import { formatCurrency } from 'src/app/core/utils/currency.utils';
import { CurrencyService } from 'src/app/core/services/currency.service';
import { AnalyticsService } from 'src/app/analytics/analytics.service';
import { AnalyticsEvent } from 'src/app/analytics/models/analytics-event';
import { AddStockManuallyModalComponent } from '../add-stock-manually-modal/add-stock-manually-modal.component';
import { AddStockDialogData } from '../add-stock-manually-modal/models/add-stock-manually-model';
import { StoreStockLotsService } from '../../services/store-stock-lots.service';

// interface LocalStockItem extends StockItem {
//   currency?: string | null;
// }

export interface StockTableConfig {
  storeId?: number;
  showStoreFilter?: boolean;
  showPendingOrders?: boolean;
  showAlertsLink?: boolean;
  showCreateButton?: boolean;
  title?: string;
  isStorageDetailContext?: boolean;
}

interface StoreOption {
  value: number | null;
  label: string;
}

@Component({
  selector: 'app-stock-table',
  templateUrl: './stock-table.component.html',
  styleUrls: ['./stock-table.component.scss']
})
export class StockTableComponent implements OnInit, OnDestroy {
  @Input() config: StockTableConfig = {
    showStoreFilter: true,
    showPendingOrders: true,
    showAlertsLink: true,
    showCreateButton: true
  };

  @Output() deliveredOrdersClicked = new EventEmitter<void>();
  @Output() alertsClicked = new EventEmitter<void>();

  private readonly destroy$ = new Subject<void>();

  // subscription tracking for stock requests
  private stockSub: Subscription | null = null;

  // Data
  stock: StoreStockItem[] = [];
  stores: StorageResponse[] = [];
  productAlerts = new Map<number, ProductAlert>();

  // UI state
  searchTerm = '';
  storeFilter: number | null = null;
  possibleStores: StoreOption[] = [];
  isLoading = false;
  deliveredOrdersLoading = false;
  showDeliveredOrdersNotification = false;
  showAddProductsMenu = false;
  expandedStockIndex: number | null = null;

  // pagination
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  hasPrevious = false;
  hasNext = false;

  // auth/role
  isCenterAdmin = false;
  currentUserId: number | null = null;
  currentUserCenterId: number | null = null;

  // delivered orders
  deliveredOrdersCount = 0;

  private readonly defaultConfig: Required<Pick<
    StockTableConfig,
    'showStoreFilter' | 'showPendingOrders' | 'showAlertsLink' | 'showCreateButton'
  >> = {
      showStoreFilter: true,
      showPendingOrders: true,
      showAlertsLink: true,
      showCreateButton: true
    };

  get resolvedConfig(): StockTableConfig & typeof this.defaultConfig {
    return {
      ...this.defaultConfig,
      ...this.config
    };
  }

  private get effectiveStoreId(): number | undefined {
    const id = this.config?.storeId ?? this.storeFilter ?? null;
    return typeof id === 'number' && id > 0 ? id : undefined;
  }

  constructor(
    private readonly storeStockLotsService: StoreStockLotsService,
    private readonly storageService: StorageService,
    private readonly ordersService: OrdersService,
    private readonly dialog: MatDialog,
    private readonly userRoleService: UserRoleService,
    private readonly translate: TranslateService,
    private readonly drawerService: DrawerService,
    private readonly stockAlertsService: StockAlertsService,
    private readonly currencyService: CurrencyService,
    private readonly analytics: AnalyticsService
  ) { }

  ngOnInit(): void {
    this.loadUserData();
    this.loadStores();
    this.setupDrawerSubscription();

    if (this.config.storeId) {
      this.storeFilter = this.config.storeId;
    }

    this.analytics.track(AnalyticsEvent.StockView, {
      ...this.analytics.globalProps(),
      warehouse_id: this.storeFilter ?? undefined
    });

    this.setSearcher();
  }



  ngOnDestroy(): void {
    this.stockSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupDrawerSubscription(): void {
    this.drawerService.onClose$
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result && this.config.showPendingOrders) {
          this.loadDeliveredOrders();
        }
      });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (this.showAddProductsMenu && !this.isClickInsideComponent(target, 'dropdown-container')) {
      this.closeAddProductsMenu();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onDocumentEscape(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (this.showAddProductsMenu) {
      keyboardEvent.preventDefault();
      this.closeAddProductsMenu();
    }
  }

  private isClickInsideComponent(target: HTMLElement, className: string): boolean {
    return Boolean(target.closest(`.${className}`));
  }

  private loadUserData(): void {
    this.userRoleService.getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          if (!user) return;
          this.currentUserId = user.id;
          this.currentUserCenterId = user.centerId;
          this.isCenterAdmin = user.roles.includes('CenterAdmin');
          this.loadStock();
          if (this.config.showPendingOrders) {
            this.loadDeliveredOrders();
          }
        },
        error: (err) => {
          console.error('Error loading user data:', err);
        }
      });
  }

  private loadStores(): void {
    this.storageService.getAllStorages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stores) => {
          this.stores = stores;
          this.setupStoreFilter();
        },
        error: (err) => {
          console.error('Error loading stores:', err);
        }
      });
  }

  private setupStoreFilter(): void {
    this.possibleStores = [
      { value: null, label: this.translate.instant('pages.stock.store.all') },
      ...this.stores.map(s => ({ value: s.id, label: s.name }))
    ];
  }

  /**
   * Wire the internal searchSubject to `loadStock`.  The stream is
   * debounced so rapid keystrokes don't trigger immediate calls, and
   * each emission simply updates the search term and invokes `loadStock`
   * which itself handles cancelling any in-flight request.
   */
  private setSearcher(): void {
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((search) => {
        this.searchTerm = search;
        this.currentPage = 1;
        this.loadStock();
      });
  }

  loadStock(): void {
    if (this.stockSub) {
      this.stockSub.unsubscribe();
      this.stockSub = null;
    }

    this.isLoading = true;
    const searchText = this.searchTerm?.trim() || undefined;
    const storeId = this.effectiveStoreId;

    this.stockSub = this.storeStockLotsService
      .getTyreStockPaginated(this.currentPage, this.pageSize, storeId, searchText)
      .pipe(
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (response) => this.handleStockResponse(response),
        error: (err) => console.error('Error loading stock:', err)
      });
  }

  private handleStockResponse(response: InventoryResponse): void {
    if (!response) {
      this.resetPagination();
      this.stock = [];
      return;
    }

    this.stock = [...response.items];

    this.totalPages = response.totalPages ?? 0;
    this.totalItems = response.totalCount ?? 0;
    this.currentPage = response.currentPage ?? this.currentPage;
    this.pageSize = response.pageSize ?? this.pageSize;
    this.hasPrevious = this.currentPage > 1;
    this.hasNext = this.totalPages > this.currentPage;
  }

  private resetPagination(): void {
    this.totalPages = 0;
    this.totalItems = 0;
    this.currentPage = 1;
    this.hasPrevious = false;
    this.hasNext = false;
  }

  private loadDeliveredOrders(): void {
    this.deliveredOrdersLoading = true;

    this.ordersService
      .getOrders(
        1,
        100,
        1,
        undefined,
        undefined,
        this.currentUserCenterId ?? undefined
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const countFromMetadata = response?.metadata?.totalCount;
          const countFromItems = Array.isArray(response?.items) ? response.items.length : undefined;
          const count = typeof countFromMetadata === 'number'
            ? countFromMetadata
            : typeof countFromItems === 'number'
              ? countFromItems
              : 0;

          this.deliveredOrdersCount = count;
          this.showDeliveredOrdersNotification = this.deliveredOrdersCount > 0;
          this.deliveredOrdersLoading = false;
        },
        error: (err: unknown) => {
          console.error('Error loading delivered orders:', err);
          this.deliveredOrdersCount = 0;
          this.showDeliveredOrdersNotification = false;
          this.deliveredOrdersLoading = false;
        }
      });
  }

  getAlertsForProduct(productId: number): ProductAlert | null {
    return this.productAlerts.get(productId) ?? null;
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadStock();
  }
  private searchSubject = new Subject<string>();

  onSearch(value: string) {
    this.currentPage = 1;
    this.searchSubject.next(value);
  }

  setPage(page: number): void {
    this.currentPage = page;
    this.expandedStockIndex = null;
    this.loadStock();
  }

  getStockStatusClass(quantity: number): string {
    if (quantity <= 0) return 'no-stock';
    if (quantity <= 5) return 'low-stock';
    return 'good-stock';
  }

  toggleStockLots(index: number): void {
    if (this.expandedStockIndex === index) {
      this.expandedStockIndex = null;
    } else {
      this.expandedStockIndex = index;
    }
  }

  onRowKeydown(event: Event, index: number): void {
    const keyboardEvent = event as KeyboardEvent;
    const target = keyboardEvent.target as HTMLElement | null;
    if (target?.closest('button, a, input, select, textarea')) {
      return;
    }
    if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
      keyboardEvent.preventDefault();
      this.toggleStockLots(index);
    }
  }

  editStockLot(lotId: number): void {
    const ref = this.dialog.open(StockLotModalComponent, {
      width: '600px',
      autoFocus: false,
      data: {
        lotId: lotId
      }
    });

    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(res => {
      if (res) this.loadStock();
    });
  }

  openAddManualy(event?: Event, item?: StoreStockItem | null): void {
    event?.stopPropagation()
    const dialogRef = this.dialog.open(AddStockManuallyModalComponent, {
      width: '600px',
      height: '90vh',
      panelClass: 'add-stock-modal-panel',
      data: {
        title: this.translate.instant("addStock.add_stock"),
        selectedStoreId: item?.storeId,
        selectedProductId: item?.productId,
        productType: 'existing'
      } as AddStockDialogData
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe((result: unknown) => {
        if (result) {
          this.loadStock();
        }
      });
  }

  openTranfer(stockLot: StoreStockLot, storeStock: StoreStockItem): void {
    stockLot.storeName = storeStock.storeName;
    const req: StoreStockAndLot = {
      stock: storeStock,
      lot: stockLot
    }

    this.drawerService.openEditStockDrawer(req);

    this.drawerService.onClose$
      .pipe(take(1))
      .subscribe((data) => {
        if (data) {
          this.loadStock();
        }
      });
  }

  deleteStockLot(stockLot: StoreStockLot): void {
    const ref = this.dialog.open(ConfirmMessageComponent, {
      maxWidth: '350px',
      minWidth: '300px',
      panelClass: 'rounded-dialog',
      data: {
        title: this.translate.instant('pages.stock.deleteLot'),
        message: this.translate.instant('pages.stock.deleteLotConfirmation'),
        confirmText: this.translate.instant('common.delete'),
        cancelText: this.translate.instant('common.cancel'),
        isDelete: true
      }
    });

    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.storeStockLotsService.deleteStockLot(stockLot.lotId).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => this.loadStock(),
        error: (err) => {
          console.error('Error deleting stock lot:', err);
        }
      });
    });
  }

  onAlertsClick(): void {
    this.alertsClicked.emit();
  }


  openExportStockDrawer(): void {
    const drawerData: { isStorageDetailContext?: boolean; storeId?: number; storeName?: string; searchText?: string } = {};

    if (this.config.isStorageDetailContext && this.config.storeId) {
      drawerData.isStorageDetailContext = true;
      drawerData.storeId = this.config.storeId;
      const store = this.stores.find(s => s.id === this.config.storeId);
      if (store) drawerData.storeName = store.name;
    }

    const searchText = this.searchTerm?.trim();
    if (searchText) {
      drawerData.searchText = searchText;
    }

    if (!drawerData.storeId && this.storeFilter) {
      drawerData.storeId = this.storeFilter;
      const store = this.stores.find(s => s.id === this.storeFilter);
      if (store) drawerData.storeName = store.name;
    }

    this.drawerService.openExportStockDrawer(drawerData);
  }


  formatPrice(amount: number | null | undefined): string {
    if (amount === null || amount === undefined) return '';
    const userCurrency = this.currencyService.getCurrentCurrencyCode();
    return formatCurrency(amount, userCurrency);
  }

  toggleAddProductsMenu(): void {
    this.showAddProductsMenu = !this.showAddProductsMenu;
  }

  closeAddProductsMenu(): void {
    this.showAddProductsMenu = false;
  }

  openBulkUploadModal(): void {
    const modalData: { storeId?: number; storeName?: string } = {};
    if (this.config.storeId) {
      modalData.storeId = this.config.storeId;
      const store = this.stores.find(s => s.id === this.config.storeId);
      if (store) {
        modalData.storeName = store.name;
      }
    }

    const ref = this.dialog.open(BulkUploadModalComponent, {
      width: '600px',
      maxHeight: '90vh',
      disableClose: true,
      data: modalData
    });

    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) this.loadStock();
    });
  }

  trackByStock(index: number, item: StoreStockItem): number {
    return item.productId ?? index;
  }

  trackByLot(index: number, lot: StoreStockLot): number {
    return lot.lotId ?? index;
  }

}
